import { useMemo } from 'react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { CallIntelligenceOrgWetags } from '@wetags/call-intelligence';
import { useBackfillStatus } from '@frontend/call-intel-backfill';
import { useCallIntelSubscription, useHasCallIntelAclAccess } from '@frontend/call-intel-core';
import { usePreference } from '@frontend/user-preferences';
import { TERMINAL_ONBOARDING_STATUSES } from '../constant';
import { useProOnboardingProgress } from './use-pro-onboarding-progress';

export function useProOnboardingEligibility() {
  const [lifecycle] = usePreference('CIProOnboardingLifecycle');
  const isPermanentlyHidden = !!lifecycle.hiddenAt;

  const { isEnabled: isFeatureEnabled, isLoading: isWetagLoading } = CallIntelligenceOrgWetags.useWetagQuery(
    'call-intel-pro-plan-onboarding'
  );

  const {
    isCallIntelProActive,
    isLoading: isSubscriptionLoading,
    callIntelProPlanLocationIds,
  } = useCallIntelSubscription({ includeAiReceptionist: false });

  const hasAclAccess = useHasCallIntelAclAccess([Permission.CALL_INTELLIGENCE_READ], { allowDemoOverride: false });

  const { isFirstBatchBackfillCompleted, isLoading: isBackfillLoading } =
    useBackfillStatus(callIntelProPlanLocationIds);

  const basicEligibilityLoading = isWetagLoading || isSubscriptionLoading || isBackfillLoading;

  const passesBasicEligibility =
    !basicEligibilityLoading &&
    !isPermanentlyHidden &&
    !!isFeatureEnabled &&
    !!hasAclAccess &&
    !!isCallIntelProActive &&
    !!isFirstBatchBackfillCompleted;

  const {
    onboardingStatus,
    isLoading: isOnboardingStatusLoading,
    hasOnboardingStatusError,
  } = useProOnboardingProgress({ enabled: passesBasicEligibility });

  const isLoading = basicEligibilityLoading || (passesBasicEligibility && isOnboardingStatusLoading);

  return useMemo(() => {
    if (isLoading) {
      return { isEligible: false, isLoading: true };
    }

    if (!passesBasicEligibility) {
      return { isEligible: false, isLoading: false };
    }

    if (hasOnboardingStatusError) {
      return { isEligible: false, isLoading: false };
    }

    if (onboardingStatus && TERMINAL_ONBOARDING_STATUSES.includes(onboardingStatus)) {
      return { isEligible: false, isLoading: false };
    }

    return { isEligible: true, isLoading: false };
  }, [isLoading, passesBasicEligibility, hasOnboardingStatusError, onboardingStatus]);
}
